import { api } from "./client";
import { unwrapApiData } from "./unwrap";
import { ApiClientError } from "./types";

export interface MonthlySalesTrendPoint {
  month: string;
  totalMinor: number;
}

export interface InventoryDistributionBucket {
  itemType: number;
  itemCount: number;
  totalValueMinor: number;
}

export interface DashboardKpis {
  orgId: string;
  startDate: string;
  endDate: string;
  totalSalesMinor: number;
  totalExpensesMinor: number;
  pendingWorkOrdersCount: number;
  activeEmployeesCount: number;
  cashBalanceMinor: number;
  bankBalanceMinor: number;
  receivablesMinor: number;
  payablesMinor: number;
  monthlySalesTrend: MonthlySalesTrendPoint[];
  inventoryDistribution: InventoryDistributionBucket[];
}

export interface AiInsight {
  insightId: string;
  orgId: string;
  generatedAt: string;
  contextSnapshotJson: string;
  aiResponseMarkdown: string;
  isActioned: number;
}

export interface GenerateAiInsightResult {
  insightId: string;
  orgId: string;
  generatedAt: string;
  aiResponseMarkdown: string;
  contextSnapshot: DashboardKpis;
  model: string;
}

export interface PinnedAccount {
  pinId: string;
  accountId: string;
  seqIdx: number;
  colorTag: string | null;
  createdAt: string;
}

export interface PinnedAccountBalance extends PinnedAccount {
  accountCode: string;
  nameI18nKey: string;
  nameCustom?: string | null;
  displayName?: string;
  accountType: number;
  balanceMinor: number;
  creditNormal: boolean;
}

interface DashboardKpisRow {
  orgId: string;
  startDate: string;
  endDate: string;
  totalSalesMinor: number;
  totalExpensesMinor: number;
  pendingWorkOrdersCount: number;
  activeEmployeesCount: number;
  cashBalanceMinor: number;
  bankBalanceMinor: number;
  receivablesMinor: number;
  payablesMinor: number;
  monthlySalesTrend: MonthlySalesTrendPoint[];
  inventoryDistribution: InventoryDistributionBucket[];
}

interface AiInsightRow {
  insightId: string;
  orgId: string;
  generatedAt: string;
  contextSnapshotJson: string;
  aiResponseMarkdown: string;
  isActioned: number;
}

interface GenerateAiInsightRow {
  insightId: string;
  orgId: string;
  generatedAt: string;
  aiResponseMarkdown: string;
  contextSnapshot: DashboardKpisRow;
  model: string;
}

interface PinnedAccountRow {
  pinId: string;
  accountId: string;
  seqIdx: number;
  colorTag: string | null;
  createdAt: string;
}

interface PinnedAccountBalanceRow extends PinnedAccountRow {
  accountCode: string;
  nameI18nKey: string;
  nameCustom?: string | null;
  displayName?: string;
  accountType: number;
  balanceMinor: number;
  creditNormal: boolean;
}

function buildQuery(params: Record<string, string>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.trim()) {
      search.set(key, value.trim());
    }
  }
  const queryString = search.toString();
  return queryString ? `?${queryString}` : "";
}

function mapKpis(row: DashboardKpisRow): DashboardKpis {
  return {
    orgId: row.orgId,
    startDate: row.startDate,
    endDate: row.endDate,
    totalSalesMinor: row.totalSalesMinor,
    totalExpensesMinor: row.totalExpensesMinor,
    pendingWorkOrdersCount: row.pendingWorkOrdersCount,
    activeEmployeesCount: row.activeEmployeesCount,
    cashBalanceMinor: row.cashBalanceMinor,
    bankBalanceMinor: row.bankBalanceMinor,
    receivablesMinor: row.receivablesMinor,
    payablesMinor: row.payablesMinor,
    monthlySalesTrend: row.monthlySalesTrend ?? [],
    inventoryDistribution: row.inventoryDistribution ?? [],
  };
}

function mapInsight(row: AiInsightRow): AiInsight {
  return {
    insightId: row.insightId,
    orgId: row.orgId,
    generatedAt: row.generatedAt,
    contextSnapshotJson: row.contextSnapshotJson,
    aiResponseMarkdown: row.aiResponseMarkdown,
    isActioned: row.isActioned,
  };
}

function mapPinned(row: PinnedAccountRow): PinnedAccount {
  return {
    pinId: row.pinId,
    accountId: row.accountId,
    seqIdx: row.seqIdx,
    colorTag: row.colorTag ?? null,
    createdAt: row.createdAt,
  };
}

function mapPinnedBalance(row: PinnedAccountBalanceRow): PinnedAccountBalance {
  return { ...mapPinned(row), accountCode: row.accountCode, nameI18nKey: row.nameI18nKey, accountType: row.accountType, balanceMinor: row.balanceMinor, creditNormal: !!row.creditNormal };
}

export async function getKpis(
  startDate: string,
  endDate: string,
): Promise<DashboardKpis> {
  const start = startDate.trim();
  const end = endDate.trim();
  if (!start) {
    throw new Error("startDate is required");
  }
  if (!end) {
    throw new Error("endDate is required");
  }

  const query = buildQuery({ startDate: start, endDate: end });
  const res = await api.get<
    { ok: true; data: DashboardKpisRow } | import("./types").ApiErrorBody
  >(`/v1/dashboard/kpis${query}`);

  return mapKpis(unwrapApiData(res));
}

export async function getLatestAiInsight(): Promise<AiInsight | null> {
  const res = await api.get<
    | { ok: true; data: AiInsightRow | null; meta?: Record<string, unknown> }
    | import("./types").ApiErrorBody
  >("/v1/dashboard/ai-insight/latest");

  const payload = unwrapApiData(res);
  return payload ? mapInsight(payload) : null;
}

export async function generateAiInsight(
  startDate: string,
  endDate: string,
): Promise<GenerateAiInsightResult> {
  const start = startDate.trim();
  const end = endDate.trim();
  if (!start) {
    throw new Error("startDate is required");
  }
  if (!end) {
    throw new Error("endDate is required");
  }

  const res = await api.post<
    { ok: true; data: GenerateAiInsightRow } | import("./types").ApiErrorBody
  >("/v1/dashboard/ai-insight", {
    startDate: start,
    endDate: end,
  });

  const row = unwrapApiData(res);
  return {
    insightId: row.insightId,
    orgId: row.orgId,
    generatedAt: row.generatedAt,
    aiResponseMarkdown: row.aiResponseMarkdown,
    contextSnapshot: mapKpis(row.contextSnapshot),
    model: row.model,
  };
}

// ================== Pinned Accounts (V4) ==========================
export async function listPinnedAccounts(): Promise<PinnedAccount[]> {
  const res = await api.get<
    { ok: true; data: PinnedAccountRow[] } | import("./types").ApiErrorBody
  >("/v1/dashboard/pins");
  return (unwrapApiData(res) ?? []).map(mapPinned);
}

export async function listPinnedBalances(asOf?: string): Promise<{ balances: PinnedAccountBalance[]; asOf: string | null }> {
  const q = asOf?.trim() ? buildQuery({ asOf: asOf.trim() }) : "";
  const res = await api.get<
    | { ok: true; data: PinnedAccountBalanceRow[]; asOf: string | null }
    | import("./types").ApiErrorBody
  >(`/v1/dashboard/pins/balances${q}`);
  if (!("ok" in res) || res.ok !== true) {
    const err = res as import("./types").ApiErrorBody;
    const e = err.error;
    throw new ApiClientError(
      e?.message || "Failed to load pinned balances",
      e?.code || 500,
      e?.code || 500,
      e?.reason,
    );
  }
  return {
    balances: (res.data ?? []).map(mapPinnedBalance),
    asOf: res.asOf ?? null,
  };
}

export async function pinAccount(
  accountId: string,
  options?: { colorTag?: string | null; seqIdx?: number },
): Promise<PinnedAccount> {
  const res = await api.post<
    { ok: true; data: PinnedAccountRow } | import("./types").ApiErrorBody
  >("/v1/dashboard/pins", {
    accountId,
    colorTag: options?.colorTag ?? null,
    seqIdx: options?.seqIdx ?? 0,
  });
  return mapPinned(unwrapApiData(res));
}

export async function updatePin(
  pinId: string,
  patch: { seqIdx?: number; colorTag?: string | null },
): Promise<Pick<PinnedAccount, "pinId" | "seqIdx" | "colorTag">> {
  const res = await api.patch<
    | { ok: true; data: { pinId: string; seqIdx: number; colorTag: string | null } }
    | import("./types").ApiErrorBody
  >(`/v1/dashboard/pins/${encodeURIComponent(pinId)}`, patch);
  return unwrapApiData(res);
}

export async function unpinAccount(pinId: string): Promise<void> {
  const res = await api.delete<
    { ok: true; data?: unknown } | import("./types").ApiErrorBody
  >(`/v1/dashboard/pins/${encodeURIComponent(pinId)}`);
  if (!("ok" in res) || res.ok !== true) {
    const err = res as import("./types").ApiErrorBody;
    const e = err.error;
    throw new ApiClientError(
      e?.message || "Failed to unpin account",
      e?.code || 500,
      e?.code || 500,
      e?.reason,
    );
  }
}

